/**
 * Menu — lightweight dropdown menu in the galactic theme.
 *
 * Renders the menu via a React portal on document.body so it can escape
 * `overflow-hidden` ancestors (e.g. tables inside rounded Cards) and is
 * positioned with `position: fixed` against the trigger's bounding box.
 * Smart placement: opens downward by default, flips upward if there isn't
 * enough room below the viewport.
 *
 * Usage:
 *   <Menu trigger={<MoreVertical className="size-4" />}>
 *     <MenuItem icon={Eye} onClick={...}>Anteprima</MenuItem>
 *     <MenuItem icon={Trash2} tone="danger" onClick={...}>Elimina</MenuItem>
 *     <MenuDivider />
 *     <MenuItem disabled>Esporta (presto)</MenuItem>
 *   </Menu>
 */
import type { LucideIcon } from 'lucide-react'
import {
  type KeyboardEvent,
  type ReactNode,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'
import { createPortal } from 'react-dom'
import { cn } from '../../lib/utils'

const MENU_MIN_WIDTH = 200
const VIEWPORT_PADDING = 8 // keep this many px away from the viewport edges

export function Menu({
  trigger,
  children,
  align = 'right',
  triggerTitle,
  triggerClassName,
}: {
  trigger: ReactNode
  children: ReactNode
  align?: 'left' | 'right'
  triggerTitle?: string
  triggerClassName?: string
}) {
  const [open, setOpen] = useState(false)
  const [coords, setCoords] = useState<{
    top: number
    left: number
    placement: 'down' | 'up'
  } | null>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)
  const menuId = useId()

  // Compute the menu position from the trigger's bounding box.
  // - Default placement: below the trigger.
  // - If the menu would overflow the viewport bottom, flip above.
  // - Horizontally align to trigger's right edge (align='right') or left.
  const recomputePosition = useCallback(() => {
    const trig = triggerRef.current
    if (!trig) return
    const tr = trig.getBoundingClientRect()
    const menuEl = menuRef.current
    const menuW = menuEl?.offsetWidth ?? MENU_MIN_WIDTH
    const menuH = menuEl?.offsetHeight ?? 240

    // Vertical: prefer below; flip above if would overflow viewport.
    const spaceBelow = window.innerHeight - tr.bottom
    const placement: 'down' | 'up' =
      spaceBelow < menuH + VIEWPORT_PADDING && tr.top > menuH + VIEWPORT_PADDING ? 'up' : 'down'
    const top = placement === 'down' ? tr.bottom + 4 : tr.top - menuH - 4

    // Horizontal: align right-edge to trigger right (align='right') OR
    // align left-edge to trigger left. Clamp to viewport.
    let left = align === 'right' ? tr.right - menuW : tr.left
    if (left + menuW > window.innerWidth - VIEWPORT_PADDING) {
      left = window.innerWidth - menuW - VIEWPORT_PADDING
    }
    if (left < VIEWPORT_PADDING) left = VIEWPORT_PADDING

    setCoords({ top, left, placement })
  }, [align])

  // Recompute on open + on scroll/resize while open.
  useLayoutEffect(() => {
    if (!open) return
    recomputePosition()
  }, [open, recomputePosition])

  useEffect(() => {
    if (!open) return
    function onScrollOrResize() {
      recomputePosition()
    }
    window.addEventListener('scroll', onScrollOrResize, true)
    window.addEventListener('resize', onScrollOrResize)
    return () => {
      window.removeEventListener('scroll', onScrollOrResize, true)
      window.removeEventListener('resize', onScrollOrResize)
    }
  }, [open, recomputePosition])

  // Click-outside (trigger AND menu) + Escape.
  useEffect(() => {
    if (!open) return
    function onPointer(e: MouseEvent) {
      const t = e.target as Node
      if (triggerRef.current?.contains(t)) return
      if (menuRef.current?.contains(t)) return
      setOpen(false)
    }
    function onKey(e: globalThis.KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onPointer)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onPointer)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  const close = useCallback(() => setOpen(false), [])

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={menuId}
        title={triggerTitle}
        onClick={() => setOpen((o) => !o)}
        className={cn(
          'p-1.5 rounded-md transition text-zinc-400 hover:bg-zinc-800/60 hover:text-zinc-100',
          open && 'bg-zinc-800/80 text-zinc-100',
          triggerClassName,
        )}
      >
        {trigger}
      </button>
      {open &&
        createPortal(
          <div
            ref={menuRef}
            id={menuId}
            role="menu"
            style={{
              position: 'fixed',
              top: coords?.top ?? -9999,
              left: coords?.left ?? -9999,
              minWidth: MENU_MIN_WIDTH,
            }}
            className={cn(
              'z-50 py-1',
              'rounded-md border border-zinc-700/60 bg-zinc-900/95 backdrop-blur-md',
              'shadow-[0_8px_24px_-8px_rgba(0,0,0,0.6),0_0_0_1px_rgba(34,211,238,0.08)_inset]',
            )}
            onClick={close}
            onKeyDown={(e: KeyboardEvent<HTMLDivElement>) => {
              if (e.key === 'Enter' || e.key === ' ') close()
            }}
          >
            {children}
          </div>,
          document.body,
        )}
    </>
  )
}

export function MenuItem({
  children,
  onClick,
  icon: Icon,
  tone,
  disabled,
}: {
  children: ReactNode
  onClick?: () => void
  icon?: LucideIcon
  tone?: 'success' | 'danger' | 'default'
  disabled?: boolean
}) {
  const toneCls =
    tone === 'success'
      ? 'text-emerald-300 hover:bg-emerald-500/10'
      : tone === 'danger'
        ? 'text-pink-300 hover:bg-pink-500/10'
        : 'text-zinc-200 hover:bg-zinc-800/80'
  return (
    <button
      type="button"
      role="menuitem"
      disabled={disabled}
      onClick={onClick}
      className={cn(
        'w-full flex items-center gap-2.5 px-3 py-1.5 text-sm text-left transition',
        toneCls,
        disabled && 'opacity-40 cursor-not-allowed hover:bg-transparent',
      )}
    >
      {Icon && <Icon className="size-3.5 shrink-0" />}
      <span className="flex-1">{children}</span>
    </button>
  )
}

export function MenuDivider() {
  return <div className="my-1 border-t border-zinc-800/80" />
}

export function MenuSectionLabel({ children }: { children: ReactNode }) {
  return (
    <div className="px-3 pt-1.5 pb-0.5 text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">
      {children}
    </div>
  )
}
